import { Stack } from "@mantine/core"
import { IconEdit, IconTrash } from "@tabler/icons-react"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { ActionIcon, Missing, TextCell } from "@tfg/ui"
import { useMemo } from "react"

import { AdminResponses } from "@tfg/types/endpoints/admin"

import { useActivitiesContext } from "~/components/contexts/useActivitiesContext"
import { useOrganizationsContext } from "~/pages/Admin/MasterLists/CustomFields/contexts/useOrganizationsContext"

const columnHelper = createColumnHelper<AdminResponses.CustomFieldDefinition>()
export const useColumns = (
  handleDelete: (id: number) => void,
  handleUpdate: (customField: AdminResponses.CustomFieldDefinition) => void
) => {
  const activities = useActivitiesContext()
  const organizations = useOrganizationsContext()

  return useMemo<ColumnDef<AdminResponses.CustomFieldDefinition, any>[]>(
    () => [
      columnHelper.accessor("id", {
        header: "ID",
        cell: ({ getValue }) => <TextCell value={getValue()} />,
      }),
      columnHelper.accessor(
        (row) => {
          const activity = activities.find(
            (activity) => activity.id === row.forActivityId
          )
          return activity?.name
        },
        {
          header: "For Activity",
          cell: ({ getValue }) => <TextCell value={getValue()} />,
        }
      ),
      columnHelper.accessor("enabledForOrganizationsIds", {
        header: "Organizations",
        cell: ({ getValue }) => {
          const enabledOrganizations = organizations.filter((organization) =>
            getValue().includes(organization.id)
          )
          return enabledOrganizations.length > 0 ? (
            <TextCell
              value={enabledOrganizations
                .map((organization) => organization.name)
                .join(", ")}
            />
          ) : (
            <Missing />
          )
        },
      }),
      columnHelper.accessor("textContent.originalText", {
        header: "Original Text",
        cell: ({ getValue }) => <TextCell value={getValue()} />,
      }),
      columnHelper.accessor(
        (row) => {
          const translation = row.textContent.TextTranslation.find(
            ({ translatedLanguage }) => translatedLanguage === "EN"
          )
          return translation?.translation ?? ""
        },
        {
          header: "EN Translation",
          cell: ({ getValue }) => <TextCell value={getValue()} />,
        }
      ),
      columnHelper.accessor(
        (row) => {
          const translation = row.textContent.TextTranslation.find(
            ({ translatedLanguage }) => translatedLanguage === "FR"
          )
          return translation?.translation ?? ""
        },
        {
          header: "FR Translation",
          cell: ({ getValue }) => <TextCell value={getValue()} />,
        }
      ),
      columnHelper.accessor("constraint", {
        header: "Constraint",
        cell: ({ getValue }) => <TextCell value={getValue()} />,
      }),
      columnHelper.accessor("enumValuesOriginalText", {
        header: "Enum Values",
        cell: ({ getValue }) => (
          <Stack>
            <TextCell
              value={getValue()
                .map(
                  (enumValue: { originalText: string }) =>
                    enumValue.originalText
                )
                .join(", ")}
            />
          </Stack>
        ),
      }),
      columnHelper.accessor((row) => ({ min: row.min, max: row.max }), {
        header: "Min-Max",
        cell: ({ getValue }) => {
          const { min, max } = getValue()

          return min === null && max === null ? (
            <Missing />
          ) : (
            <TextCell value={`${min ?? ""}-${max ?? ""}`} />
          )
        },
      }),

      columnHelper.accessor("message", {
        header: "Message",
        cell: ({ getValue }) => <TextCell value={getValue()?.originalText} />,
      }),
      columnHelper.accessor("onActivityId", {
        header: "On Activity",
        cell: ({ getValue }) => {
          const activity = activities.find(
            (activity) => activity.id === getValue()
          )
          return activity?.name
        },
      }),
      columnHelper.accessor((row) => row.id, {
        header: () => null,
        id: "action-cell-delete",
        cell: ({ row }) => (
          <ActionIcon
            icon={<IconTrash />}
            onClick={() => handleDelete(row.original.id)}
          />
        ),
      }),
      columnHelper.accessor((row) => row.id, {
        header: () => null,
        id: "action-cell-update",
        cell: ({ row }) => (
          <ActionIcon
            icon={<IconEdit />}
            onClick={() => handleUpdate(row.original)}
          />
        ),
      }),
    ],
    [handleDelete, handleUpdate, activities, organizations]
  )
}
